<script setup>
// Astra DS · StoreBadge — el botón de tienda de la cabecera.
//
// Figma: `StoreBadge 1798:196`, propiedad `Tienda` con `TestFlight` y
// `Google Play`. Entrega `docs/entregas/2026-09-09-botones-de-tienda.md`.
//
// ── Por qué no imita al badge de Apple ──
//
// El modelo que pasó Pablo era el par clásico «Descárgalo en el App Store» /
// «Disponible en Google Play». El de iOS **no lo copia**: lleva el logotipo de
// TestFlight y dice «Pruébala en», porque **TestFlight no es una tienda** —no se
// descarga de ahí, se entra en una beta— y porque el badge de Apple es una pieza
// suya con sus reglas de uso: imitarlo con otro texto dentro sería hacer pasar
// por oficial algo que no lo es.
//
// ── La superficie es `bg/ink`, y no vale `inverse` ──
//
// El modelo que pasó Pablo era un badge oscuro sobre fondo claro. Un negro fijo
// no sirve —no tiene token en este sistema— y `bg/inverse` tampoco: **se
// invierte**, que es justo lo que aquí no se quiere. Con él, en oscuro el badge
// salía en lino sobre el lienzo negro y gritaba más que la marca. Lo vio Pablo.
//
// `bg/ink`, `border/ink` y `text/on-ink` son tokens nuevos del 09.09.26 y
// **valen lo mismo en los dos modos**: el color que quiere el badge es el mismo
// en claro y en oscuro, y lo que faltaba era poder decirlo. En oscuro queda un
// escalón por encima del lienzo, el mismo salto con el que un módulo se separa
// del fondo.
import { computed } from "vue";
import { medir } from "@/services/medicion";
import TestFlightIcon from "@/components/icons/TestFlightIcon.vue";
import GooglePlayIcon from "@/components/icons/GooglePlayIcon.vue";

const props = defineProps({
  tienda: {
    type: String,
    required: true,
    validator: (v) => ["testflight", "google-play"].includes(v),
  },
  to: { type: String, required: true },
});

// 🔴 **Dentro de la web se navega con `router-link`, no con `<a href>`.**
// Un ancla normal a una ruta propia **recarga la página entera**, y con ella se
// reinicia la aplicación: el player se para y **se corta la emisión**. Lo vio
// Pablo pulsando el botón de TestFlight con la radio puesta.
//
// El de Google Play sí es un ancla, porque sale del sitio: ahí la recarga no
// existe y hace falta `target` y `rel`.
const esExterno = computed(() => /^https?:\/\//.test(props.to));

// 🔴 **Los atributos se montan aparte, y nunca se pasa uno en `undefined`.**
//
// Aquí había un fallo que no se veía: la plantilla hacía
// `:href="esExterno ? to : undefined"`, y un atributo heredado gana al que pinta
// el componente hijo aunque valga `undefined`. Es decir, el `href` vacío **le
// borraba a `RouterLink` el suyo**, y el badge de TestFlight salía como un `<a>`
// pelado, sin `href` ni `to`.
//
// Seguía navegando —el manejador interno de `RouterLink` no depende del
// atributo—, así que al probarlo pulsando parecía bueno. Pero un ancla sin
// `href` no es un enlace para el navegador: no recibe el cursor de mano, **no
// entra en el tabulador**, no se abre en otra pestaña ni se copia la dirección,
// y el lector de pantalla no la anuncia como enlace. Lo vio Pablo por el cursor,
// que era la punta de esto.
//
// Con dos objetos, cada rama manda solo lo suyo y no hay `undefined` que herede
// nadie.
const etiqueta = computed(() => (esExterno.value ? "a" : "router-link"));
const atributos = computed(() =>
  esExterno.value
    ? { href: props.to, target: "_blank", rel: "noopener" }
    : { to: props.to },
);
</script>

<template>
  <component
    :is="etiqueta"
    v-bind="atributos"
    class="store-badge"
    @click="medir('descarga_app', { plataforma: tienda === 'testflight' ? 'ios' : 'android' })"
  >
    <TestFlightIcon v-if="tienda === 'testflight'" class="store-badge__logo" :size="21" />
    <GooglePlayIcon v-else class="store-badge__logo store-badge__logo--gp" />

    <span class="store-badge__texto">
      <span class="store-badge__kicker">
        {{ tienda === "testflight" ? "Pruébala en" : "Disponible en" }}
      </span>
      <span class="store-badge__nombre">
        {{ tienda === "testflight" ? "TestFlight" : "Google Play" }}
      </span>
    </span>
  </component>
</template>

<style scoped lang="scss">
@use "@/assets/scss/type" as t;

.store-badge {
  display: inline-flex;
  flex: none;
  align-items: center;
  // 9 entre logo y texto, del nodo.
  gap: 9px;
  // 12 a la izquierda y 16 a la derecha: el logo ya trae su propio aire.
  padding: 4px 16px 4px 12px;
  // 🔴 **El único radio de toda la web, y está acotado a este componente.**
  // DESIGN.md § Shapes lo recoge como excepción con su razón: un badge de
  // tienda se reconoce por la forma antes que por el texto, y con esquinas
  // vivas deja de leerse como botón de descarga. Si aparece un segundo
  // redondeo en otra pieza, sigue estando mal.
  border-radius: 16px;
  background: var(--color-bg-ink);
  // 🔴 **El filete va por dentro, con `box-shadow` y no con `border`.** Un
  // borde de 1px sumaría 2 al ancho y el badge dejaría de medir 113 y 119, que
  // son medidas del nodo. `inset` dibuja dentro y no ocupa.
  //
  // No es decoración: es lo que le da definición en oscuro sin subir más el
  // fondo. Como el token es constante, aclarar el fondo lo aclararía también en
  // claro, y ahí el badge empezaba a despegarse del negro del modelo. Masa y
  // contorno.
  box-shadow: inset 0 0 0 1px var(--color-border-ink);
  color: var(--color-text-on-ink);
  text-decoration: none;
  // El alto —31— lo abraza el contenido, no se fija: 4 + 21 + 4 con el logo,
  // y el texto no pasa de ahí.

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }
}

.store-badge__logo {
  flex: none;
}

// El de Google Play viene con `height: 1em`, así que se le da el cuerpo.
.store-badge__logo--gp {
  font-size: 21px;
}

.store-badge__texto {
  display: flex;
  flex-direction: column;
  // 0 entre las dos líneas: lo pide el nodo y es lo que aprieta el badge a 31.
  gap: 0;
}

// El mixin sirve tal cual: `mono/tab` va al 5 % en Figma y a `0.05em` aquí.
// (Hubo un aviso de que el estilo estaba sobreescrito; diseño lo retiró el
// 09.09.26 al comprobarlo — había confundido su tracking con el de `mono/nano`.)
.store-badge__kicker {
  @include t.mono-tab;
  line-height: 1.4;
}

// ⚠️ **Sin mixin, y es lo que la entrega deja pendiente.** El nombre va a
// Barlow Condensed Bold 13 y la rampa `display` no baja de 17, así que no hay
// escalón que aplicar. Diseño tiene que decidir entre añadir un `display/card-sm
// 13` o subir el nombre a 17; hasta entonces van los valores del nodo a mano.
.store-badge__nombre {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.8125rem; // 13
  line-height: 0.98;
  letter-spacing: -0.01em;
  text-transform: uppercase;
}
</style>
